import { Link } from "@tanstack/react-router";
import parse, {
  domToReact,
  Element,
  type HTMLReactParserOptions,
} from "html-react-parser";
import type { PropsWithChildren } from "react";
import { cn } from "#/lib/utils";

type MarkdownProps = {
  html: string;
};

export function Markdown({ html }: MarkdownProps) {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (!(domNode instanceof Element)) {
        return undefined;
      }

      if (domNode.name === "a") {
        const href = domNode.attribs.href;

        if (href?.startsWith("/")) {
          const children = domNode.children as Parameters<typeof domToReact>[0];

          return <Link to={href}>{domToReact(children, options)}</Link>;
        }
      }

      if (domNode.name === "img") {
        const { alt = "", ...attribs } = domNode.attribs;
        const loading = domNode.attribs.loading === "eager" ? "eager" : "lazy";

        return (
          <img {...attribs} alt={alt} loading={loading} className="w-full" />
        );
      }

      return undefined;
    },
  };

  return <MarkdownWithProse>{parse(html, options)}</MarkdownWithProse>;
}

const MarkdownWithProse = ({ children }: PropsWithChildren) => (
  <div
    className={cn(
      "prose max-w-none text-[1.05rem] leading-8 text-muted",
      "prose-headings:font-serif prose-headings:font-semibold prose-headings:tracking-[-0.03em] prose-headings:text-primary",
      "prose-h2:mt-0 prose-h2:mb-4 prose-h2:text-[clamp(1.9rem,1.7rem+0.8vw,2.5rem)]",
      "prose-h3:mt-10 prose-h3:mb-3 prose-h3:text-[1.35rem]",
      "prose-p:my-0 prose-p:mb-5",
      "prose-ul:pl-5 prose-ol:pl-5",
      "prose-li:my-2",
      "prose-a:font-medium prose-a:text-secondary prose-a:no-underline hover:prose-a:text-primary",
      "prose-blockquote:my-8 prose-blockquote:border-l prose-blockquote:border-l-secondary/30 prose-blockquote:pl-5 prose-blockquote:text-primary/80",
      "prose-img:w-full",
      "prose-pre:bg-primary prose-pre:text-surface",
      "[&_.anchor]:text-inherit [&_.anchor]:no-underline mt-6",
    )}
  >
    {children}
  </div>
);
